import { useCallback } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { auth } from '../firebase';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
});

// Cache the ID token to avoid calling getIdToken() on every request.
// Firebase tokens live 1 hour; we cache for 50 minutes to stay safe.
let cachedToken: string | null = null;
let tokenExpiresAt = 0;
const TOKEN_CACHE_MS = 50 * 60 * 1000; // 50 minutes

async function getToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  if (cachedToken && Date.now() < tokenExpiresAt) return cachedToken;
  cachedToken = await user.getIdToken();
  tokenExpiresAt = Date.now() + TOKEN_CACHE_MS;
  return cachedToken;
}

api.interceptors.request.use(async (config) => {
  const token = await getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      cachedToken = null;
      tokenExpiresAt = 0;
      auth.signOut();
    }
    return Promise.reject(error);
  }
);

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: string | null;
}

export function useApi() {
  const get = useCallback(async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.get<ApiResponse<T>>(url, config);
    return response.data;
  }, []);

  const post = useCallback(async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }, []);

  const patch = useCallback(async <T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }, []);

  const del = useCallback(async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.delete<ApiResponse<T>>(url, config);
    return response.data;
  }, []);

  /** POST with FormData (multipart upload). Uses the same token cache + 401 interceptor. */
  const postForm = useCallback(async <T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await api.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }, []);

  /** GET that returns a raw Blob (for PDF/CSV downloads). */
  const getBlob = useCallback(async (url: string, config?: AxiosRequestConfig): Promise<Blob> => {
    const response = await api.get(url, { ...config, responseType: 'blob' });
    return response.data as Blob;
  }, []);

  return { get, post, patch, del, postForm, getBlob };
}

export { api };
